import { Stack, Switch, Tooltip } from '@mui/material'
import { BellIcon } from '@phosphor-icons/react'
import {
    createAccountPresentation,
    createExternalParticipantPresentation,
    tokens,
    User,
    UserBubbleList,
    type AccountDisplayData,
    type ExternalParticipantDisplayData,
} from 'ComponentLibrary'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type HeaderParticipantsProps = {
    currentUser?: User
    setIsEditingParticipants: (isEditingParticipants: boolean) => void
    participantIds: number[]
    participantUsers?: User[]
    observerIds: string[] | undefined
    observerUsers?: AccountDisplayData[]
    externalParticipantUsers?: ExternalParticipantDisplayData[]
    userBubbleTooltip?: string
    isNotifiedTooltip?: string
    isNotNotifiedTooltip?: string
    disabled?: boolean
    replaceParticipantIds: (participantIds: number[]) => void
    copy?: Partial<HeaderParticipantsCopy>
    userLinkCopy?: Partial<UserLinkCopy>
}

export type HeaderParticipantsCopy = {
    participantGroup: string
    observerGroup: string
    observerTooltip: string
}

const DEFAULT_HEADER_PARTICIPANTS_COPY: HeaderParticipantsCopy = {
    participantGroup: 'Benachrichtigte',
    observerGroup: 'Beobachter',
    observerTooltip:
        'Sie beobachten diesen Auftrag. Benachrichtigungen können hier deshalb nicht deaktiviert werden. Beenden Sie die Beobachtung auf der Auftragsseite.',
}

export const HeaderParticipants = ({
    currentUser,
    replaceParticipantIds,
    setIsEditingParticipants,
    participantIds,
    participantUsers = [],
    observerIds,
    observerUsers = [],
    externalParticipantUsers = [],
    userBubbleTooltip,
    isNotifiedTooltip,
    isNotNotifiedTooltip,
    disabled,
    copy,
    userLinkCopy,
}: HeaderParticipantsProps) => {
    const resolvedCopy = { ...DEFAULT_HEADER_PARTICIPANTS_COPY, ...copy }
    const isOrderObserver = !!currentUser && !!observerIds?.includes(currentUser.uuid)
    const isToggleDisabled = disabled || observerIds === undefined || isOrderObserver
    const participantPresentations = participantUsers.map((user) => createAccountPresentation(user))
    const observerPresentations = observerUsers.map((user) => createAccountPresentation(user))
    const externalPresentations = externalParticipantUsers.map(createExternalParticipantPresentation)
    const addCurrentUserToParticipants = () => currentUser && replaceParticipantIds([...participantIds, currentUser.id])

    const removeCurrentUserFromParticipants = () =>
        currentUser && replaceParticipantIds(participantIds.filter((id) => id !== currentUser.id))

    const handleToggleClick = () => {
        if (currentUser && participantIds.includes(currentUser.id)) {
            removeCurrentUserFromParticipants()
        } else {
            addCurrentUserToParticipants()
        }
    }

    const isNotifiedForCurrentUser = !!currentUser && (participantIds.includes(currentUser.id) || isOrderObserver)

    return (
        <>
            <UserBubbleList
                tooltip={userBubbleTooltip}
                userLinkCopy={userLinkCopy}
                presentationGroups={[
                    {
                        groupName: resolvedCopy.participantGroup,
                        presentations: participantPresentations,
                    },
                    {
                        groupName: resolvedCopy.observerGroup,
                        presentations: [...observerPresentations, ...externalPresentations],
                    },
                ]}
                onClick={() => setIsEditingParticipants(true)}
            />
            <Tooltip
                title={isOrderObserver ? resolvedCopy.observerTooltip : isNotifiedForCurrentUser ? isNotifiedTooltip : isNotNotifiedTooltip}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    borderRadius={tokens.SizingCornerM}
                    bgcolor={tokens.ColorBackgroundDark}
                    padding={`${tokens.SizingSpacing100} ${tokens.SizingSpacing150}
                         ${tokens.SizingSpacing100} ${tokens.SizingSpacing050}`}
                    sx={{ cursor: isToggleDisabled ? undefined : 'pointer' }}
                    onClick={isToggleDisabled ? undefined : handleToggleClick}
                >
                    <Switch
                        checked={isNotifiedForCurrentUser}
                        disabled={isToggleDisabled}
                        sx={{
                            '& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
                                bgcolor: tokens.ColorIconLinkSelected,
                                opacity: 1,
                            },
                            '& .MuiSwitch-thumb': {
                                bgcolor: tokens.ColorSurfaceDefault,
                            },
                        }}
                    />
                    <BellIcon size={tokens.SizingIconM} color={tokens.ColorIconBoldest} />
                </Stack>
            </Tooltip>
        </>
    )
}
